import * as Yup from 'yup';
import React, { useState, useRef, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
// import AddIcon from '@mui/icons-material/Add';
// import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { useSelector } from 'react-redux';
import CustomLocationDialog from './CustomLocation';
import CustomCategoryDialog from './CustomCategory';
import InventoryLocationAPIs from '../../../utilities/api/location/AdminLocationList';
import InventoryCategoryAPIs from '../../../utilities/api/category/AdminInventoryCategoryList';
import EquipmentDetailAPIs from '../../../utilities/api/equipment/AdminEquipmentDetail';
import dayjs from 'dayjs';
import { ImageBaseURL } from '../../../utilities/api/axios';
import { toast } from 'react-toastify';
import FormField from '../../shared/FormField';
import { KeyboardArrowDown } from '@mui/icons-material';

const availabilityList = ['Moderate', 'Very Low', 'Low', 'High', 'Always'];
const statusList = ['Operational', 'Under Maintenance', 'Out of Service'];

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  upc: Yup.string().matches(/^\d*$/, 'UPC must be a pure number.'),
  instances: Yup.number().required('Instances are required'),
  manufacturer: Yup.string().nullable(),
  location: Yup.string(),
  availability: Yup.string(),
  purchase_cost: Yup.number(),
  image: Yup.mixed().nullable(),
  category: Yup.string().nullable(),
  description: Yup.string(),
  status: Yup.string(),
  supervised: Yup.boolean(),
  purchase_date: Yup.date()
    .nullable()
    .typeError('Date has wrong format. Use YYYY-MM-DD'),
  warranty_expiration: Yup.date()
    .nullable()
    .typeError('Date has wrong format. Use YYYY-MM-DD'),
});

const styledField = {
  maxWidth: '100%',
  width: '100%',
  padding: '0px 4px 8px 6px',
  '& .MuiFormLabel-root': {
    marginLeft: '4px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '12px',
    border: '1px solid #8C8C8C',
  },
};

const headerProps = { sx: { fontWeight: '400', color: '#666666' } };

const EditEquipmentDialog = ({
  currentRow,
  open,
  onClose,
  update,
  setUpdate,
  flag,
  setFlag,
}) => {
  const user = useSelector(state => state.user.user);
  const token = user.access;

  const category = useSelector(state => state.category.category_list);
  const location = useSelector(state => state.location.location_list);
  const categories = category.map(item => item.name);
  const locations = location.map(item => item.name);

  const [openCustomLocation, setOpenCustomLocation] = useState(false);
  const [customLocation, setCustomLocation] = useState('');
  const [openCustomCategory, setOpenCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [newImage, setNewImage] = useState(null); // State for the new image
  const fileInputRef = useRef(null);

  // Pre-fill image preview if currentRow has an image
  useEffect(() => {
    if (currentRow?.image) {
      setNewImage(currentRow.image.startsWith('http') ? currentRow.image : `${ImageBaseURL}${currentRow.image}`);
    }
  }, [currentRow]);

  const handleConfirmLocation = () => {
    setOpenCustomLocation(false);
    const response = InventoryLocationAPIs.LocationListPost(
      customLocation,
      token
    );
    response
      .then(result => {
        console.log(result);
        setFlag(prev => !prev);
      })
      .catch(err => {
        console.log('error', err);
      });
  };

  const handleConfirmCategory = () => {
    setOpenCustomCategory(false);
    const response = InventoryCategoryAPIs.CategoryListPost(
      customCategory,
      token
    );
    response
      .then(result => {
        console.log(result);
        setFlag(prev => !prev);
      })
      .catch(err => {
        console.log('error', err);
      });
  };

  const initialValues = {
    id: currentRow?.id || '',
    category: currentRow?.category || null,
    location: currentRow?.location || '',
    name: currentRow?.name || '',
    upc: currentRow?.upc || '',
    manufacturer: currentRow?.manufacturer || null,
    description: currentRow?.description || '',
    image: null,
    availability: currentRow?.availability || '',
    status: currentRow?.status || '',
    supervised: currentRow?.supervised || false,
    instances: currentRow?.instances || '',
    purchase_cost: currentRow?.purchase_cost || '',
    purchase_date: currentRow?.purchase_date
      ? dayjs(currentRow.purchase_date)
      : null,
    warranty_expiration: currentRow?.warranty_expiration
      ? dayjs(currentRow.warranty_expiration)
      : null,
    // college: currentRow?.college || '',
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Equipment</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          const formData = new FormData();
          for (const key in values) {
            if (
              values.hasOwnProperty(key) &&
              key !== 'purchase_date' &&
              key !== 'warranty_expiration' &&
              key !== 'image'
            ) {
              formData.append(key, values[key]);
            }
          }
          if (values.purchase_date) {
            formData.append(
              'purchase_date',
              values.purchase_date.format('YYYY-MM-DD')
            );
          }
          if (values.warranty_expiration) {
            formData.append(
              'warranty_expiration',
              values.warranty_expiration.format('YYYY-MM-DD')
            );
          }
          if (values.image) {
            formData.append('image', values.image);
          }

          // Add availability as index (0-4)
          if (values.availability) {
            formData.append(
              'availability1',
              availabilityList.indexOf(values.availability).toString()
            );
          }

          // Add status as index (0-2)
          if (values.status) {
            formData.append(
              'status1',
              statusList.indexOf(values.status).toString()
            );
          }

          const response = EquipmentDetailAPIs.EquipmentDetailPut(
            currentRow.id,
            formData,
            token,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          response
            .then(result => {
              console.log(result);
              onClose();
              setUpdate(prev => !prev);
            })
            .catch(error => {
              console.log(error);
              alert(`${error.response.data}`);
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {({
          handleSubmit,
          values,
          touched,
          errors,
          handleChange,
          setFieldValue,
        }) => {
          const handleFileChange = event => {
            const file = event.currentTarget.files[0];

            if (file) {
              // Check if file size exceeds 1MB (1MB = 1,048,576 bytes)
              const fileSizeLimit = 1048576; // 1MB in bytes
              if (file.size > fileSizeLimit) {
                toast.error('File size should not exceed 1MB');
                return; // Stop the upload process if the file is too large
              }

              // Check if the file is an image
              if (!file.type.startsWith('image/')) {
                toast.error('Only image files are allowed!');
                return;
              }

              // If both validations pass, set the image and display a preview
              setFieldValue('image', file);
              setNewImage(URL.createObjectURL(file)); // Set the new image URL
            }
          };

          return (
            <Form onSubmit={handleSubmit} encType="multipart/form-data">
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      name="name"
                      label="Name"
                      heading="Name*"
                      sx={styledField}
                      headerProps={headerProps}
                      value={values.name}
                      onChange={handleChange}
                      error={touched.name && !!errors.name}
                      helperText={touched.name && errors.name}
                    />
                    <FormField
                      name="upc"
                      label="Enter UPC"
                      fieldType="textfield"
                      heading="UPC"
                      sx={styledField}
                      headerProps={headerProps}
                      value={values.upc}
                      onChange={handleChange}
                      error={touched.upc && !!errors.upc}
                      helperText={touched.upc && errors.upc}
                    />
                    <FormField
                      name="instances"
                      label="Enter Instances"
                      fieldType="textfield"
                      heading="Instances*"
                      sx={styledField}
                      headerProps={headerProps}
                      value={values.instances}
                      onChange={handleChange}
                      error={touched.instances && !!errors.instances}
                      helperText={touched.instances && errors.instances}
                    />
                    <FormField
                      name="manufacturer"
                      label="Enter Manufacturer Name"
                      fieldType="textfield"
                      heading="Manufacturer"
                      sx={styledField}
                      headerProps={headerProps}
                      value={values.manufacturer}
                      onChange={handleChange}
                      error={touched.manufacturer && !!errors.manufacturer}
                      helperText={touched.manufacturer && errors.manufacturer}
                    />
                    <Box>
                      <FormField
                        name="location"
                        label="Select Location"
                        fieldType="dropdown"
                        heading="Location"
                        options={locations}
                        sx={styledField}
                        value={values.location}
                        onChange={handleChange}
                        error={touched.location && !!errors.location}
                        helperText={touched.location && errors.location}
                        SelectProps={{
                          IconComponent: KeyboardArrowDown,
                        }}
                        addOption
                        handleAddOption={() => setOpenCustomLocation(true)}
                        headerProps={headerProps}
                      />
                    </Box>

                    <CustomLocationDialog
                      openCustomLocation={openCustomLocation}
                      setCustomLocation={setCustomLocation}
                      onClose={() => setOpenCustomLocation(false)}
                      onConfirm={() => {
                        setFieldValue('location', customLocation);
                        handleConfirmLocation();
                      }}
                    />
                    <FormField
                      name="purchase_cost"
                      label="Enter Purchase Cost"
                      fieldType="textfield"
                      heading="Purchase Cost"
                      sx={styledField}
                      headerProps={headerProps}
                      value={values.purchase_cost}
                      onChange={handleChange}
                      error={touched.purchase_cost && !!errors.purchase_cost}
                      helperText={touched.purchase_cost && errors.purchase_cost}
                    />

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: '400',
                        padding: '5px',
                        color: '#666666',
                      }}
                    >
                      Equipment Image
                    </Typography>
                    {newImage ? (
                      <img
                        src={newImage}
                        alt="New Upload"
                        style={{ width: '200px', height: '200px' }}
                      />
                    ) : null}
                    <Button
                      variant="contained"
                      onClick={() => fileInputRef.current.click()}
                      sx={{
                        height: newImage ? '40px' : '100px',
                        width: '150px',
                        padding: newImage ? '8px 12px' : '12px 16px',
                        margin: 1,
                        background: newImage ? '#FFE6E6' : '#E6F2FF',
                        color: '#3F3F3F',
                        border: newImage
                          ? '1px solid #FF6666'
                          : '1px solid #66B0FF',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        '&:hover': {
                          background: newImage ? '#FFE6E6' : '#E6F2FF',
                          color: '#3F3F3F',
                          border: newImage
                            ? '1px solid #FF6666'
                            : '1px solid #66B0FF',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: newImage ? 14 : 24,
                          p: 0,
                          m: 0,
                          mt: newImage ? 0 : -1,
                        }}
                      >
                        {newImage ? 'Change Image' : '+'}
                      </Typography>
                      {!newImage && (
                        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                          Upload File
                        </Typography>
                      )}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*" // Restrict file selection to images only
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    {touched.image && errors.image && (
                      <FormHelperText error>{errors.image}</FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <FormField
                        name="category"
                        label="Select Category"
                        fieldType="dropdown"
                        heading="Category"
                        options={categories}
                        sx={styledField}
                        value={values.category}
                        onChange={handleChange}
                        error={touched.category && !!errors.category}
                        helperText={touched.category && errors.category}
                        SelectProps={{
                          IconComponent: KeyboardArrowDown,
                        }}
                        addOption
                        handleAddOption={() => setOpenCustomCategory(true)}
                        headerProps={headerProps}
                      />
                    </Box>
                    <CustomCategoryDialog
                      openCustomCategory={openCustomCategory}
                      setCustomCategory={setCustomCategory}
                      onClose={() => setOpenCustomCategory(false)}
                      onConfirm={() => {
                        setFieldValue('category', customCategory);
                        handleConfirmCategory();
                      }}
                    />
                    <FormField
                      name="availability"
                      fieldType="dropdown"
                      heading="Availability"
                      label="Select Availability"
                      options={availabilityList}
                      SelectProps={{
                        IconComponent: KeyboardArrowDown,
                      }}
                      value={values.availability}
                      onChange={handleChange}
                      error={touched.availability && !!errors.availability}
                      helperText={touched.availability && errors.availability}
                      sx={styledField}
                      headerProps={headerProps}
                    />
                    <FormField
                      name="status"
                      fieldType="dropdown"
                      heading="Status"
                      label="Select Status"
                      options={statusList}
                      sx={styledField}
                      value={values.status}
                      onChange={handleChange}
                      error={touched.status && !!errors.status}
                      helperText={touched.status && errors.status}
                      SelectProps={{
                        IconComponent: KeyboardArrowDown,
                      }}
                      headerProps={headerProps}
                    />

                    <FormControlLabel
                      control={
                        <Field
                          as={Checkbox}
                          name="supervised"
                          color="primary"
                          checked={values.supervised}
                          onChange={handleChange}
                        />
                      }
                      label={
                        <Typography
                          sx={{ fontSize: '1.25rem', color: '#666666' }}
                        >
                          Supervised
                        </Typography>
                      }
                      labelPlacement="end"
                    />
                    {touched.supervised && errors.supervised && (
                      <FormHelperText error>{errors.supervised}</FormHelperText>
                    )}
                    <FormField
                      name="purchase_date"
                      label="Purchase Date"
                      fieldType="date"
                      heading="Purchase Date"
                      sx={styledField}
                      headerProps={headerProps}
                      value={values.purchase_date}
                      onChange={value => setFieldValue('purchase_date', value)}
                      format="YYYY-MM-DD"
                    />
                    <FormField
                      name="warranty_expiration"
                      label="Warranty Expiration"
                      fieldType="date"
                      heading="Warranty Expiration"
                      sx={styledField}
                      headerProps={headerProps}
                      value={values.warranty_expiration}
                      onChange={value =>
                        setFieldValue('warranty_expiration', value)
                      }
                      format="YYYY-MM-DD"
                    />
                  </Grid>
                </Grid>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '1.2rem',
                    fontWeight: 400,
                    color: '#666666',
                    mt: 4,
                  }}
                >
                  Description
                </Typography>
                <Field
                  name="description"
                  as={TextField}
                  label="Description"
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={values.description}
                  onChange={handleChange}
                  error={touched.description && !!errors.description}
                  helperText={touched.description && errors.description}
                  sx={{
                    mt: 2,
                    mb: 1,
                    '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                  }}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={onClose}
                  color="primary"
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    borderRadius: '12px',
                    borderWidth: '1px',
                    borderColor: 'action.active',
                    '&:hover': {
                      borderWidth: '1px',
                      borderColor: 'action.active',
                    },
                    mr: 1,
                  }}
                >
                  Discard
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    color: 'common.white',
                    backgroundColor: 'primary.main',
                    borderRadius: '12px',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                >
                  Save Changes
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default EditEquipmentDialog;
